import React from 'react';
import CommonCol from '../CommonCol';
import type { TKpiOverviewDataScope } from '../../pages/overview/kpiOverview/utils/uaRoiUaMediaScopeFilters';

interface IProps {
  className?: string
  style?: any
  /** 默认宽度 */
  w?: number
  /**
   * 按 kpiOverviewDataScope（tab 类型）映射宽度。
   * 未匹配到时回退使用 w。
   * @example { "all": 12, "ua_roi_ua_media": 24 }
   */
  scopeWMap?: Partial<Record<TKpiOverviewDataScope, number>>
  children: any
  props: any
  /** 框架注入的 data，包含 kpiOverviewDataScope 等状态 */
  data?: { kpiOverviewDataScope?: string; [key: string]: unknown }
}

/**
 * CommonCol 的中间层包装组件，支持基于 kpiOverviewScope（tab 类型）按映射设置宽度。
 *
 * @example
 * ```json
 * {
 *   "name": "ScopeAwareCommonCol",
 *   "props": {
 *     "w": 12,
 *     "scopeWMap": { "ua_roi_ua_media": 24 }
 *   }
 * }
 * ```
 * 含义：默认 w=12，当切换到 "UA-ROI Campaign & UA Media Channel" tab 时变为 w=24。
 */
const ScopeAwareCommonCol = (colProps: IProps) => {
  const { className, style, children, w, scopeWMap, data, ...resetProps } = colProps || {};

  const finalW = React.useMemo(() => {
    const scope = data?.kpiOverviewDataScope as TKpiOverviewDataScope | undefined;
    if (scope && scopeWMap?.[scope] != null) {
      return scopeWMap[scope];
    }
    return w;
  }, [w, scopeWMap, data?.kpiOverviewDataScope]);

  /**
   * 须将上游透传（含 EventService 的 eventData）摊到 CommonCol 顶层，
   * 否则 CommonCol 会 clone 成子节点的 `props` 属性，KpiEventMetricTrendService 读不到 eventData。
   */
  return (
    <CommonCol
      className={className}
      style={style}
      w={finalW}
      {...(resetProps as Record<string, unknown>)}
    >
      {children}
    </CommonCol>
  );
};

export default ScopeAwareCommonCol;
